import { PositiveInteger } from "../scalars.ts";
import { Redacted, Schema } from "effect";
import { Sha256Identity } from "../../identity-input.ts";
import { privateDependencyFailure } from "../../errors/private-cause.ts";

import { CandidateId, ChangeId, CommitId, RunId, TaskId, TreeId } from "../../development-identifiers.ts";

export const CandidateToken = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9._:-]{0,127}$/)))();
export const CandidateReasonText = /* @__PURE__ */ (() => Schema.String.check(Schema.makeFilter(value =>
  value.length >= 1 && value.length <= 500 && value.trim() === value && !/[\x00-\x1f\x7f]/.test(value)
)))();
export const CandidateTargetRef = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/),
  Schema.makeFilter(value => !value.includes("//") && !value.split("/").some(part => part === "." || part === ".."))
))();
export const CandidateTargetRevision = PositiveInteger;

export const CANDIDATE_MANIFEST_VERSION = 1 as const;
export const CANDIDATE_ELIGIBILITY_VERSION = 1 as const;
export const CANDIDATE_DECISION_VERSION = 1 as const;

export const CANDIDATE_OMISSION_REASONS = [
  "deferred",
  "duplicate-scope",
  "failed-policy",
  "manual-exclusion",
  "out-of-scope",
  "superseded"
] as const;

export const CandidateOmissionReason = /* @__PURE__ */ (() => Schema.Literals(CANDIDATE_OMISSION_REASONS))();
export type CandidateOmissionReason = typeof CandidateOmissionReason.Type;

export const CandidateEligibilityInput = /* @__PURE__ */ (() => Schema.Struct({
  taskId: Schema.String.pipe(Schema.mutableKey),
  headCommitId: Schema.String.pipe(Schema.mutableKey),
  successfulRunId: Schema.String.pipe(Schema.mutableKey),
}))();
export type CandidateEligibilityInput = typeof CandidateEligibilityInput.Type;

export const CandidateEligibilityEntry = /* @__PURE__ */ (() => Schema.Struct({
  taskId: TaskId,
  headCommitId: CommitId,
  successfulRunId: RunId,
}))();
export type CandidateEligibilityEntry = typeof CandidateEligibilityEntry.Type;

export const CandidateTaskDecisionInput = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  taskId: Schema.String,
  headCommitId: Schema.String,
  decision: Schema.Literal("included"),
  reason: Schema.optionalKey(Schema.Never),
  reasonDetail: Schema.optionalKey(Schema.Never),
}), Schema.Struct({
  taskId: Schema.String,
  headCommitId: Schema.String,
  decision: Schema.Literal("omitted"),
  reason: CandidateOmissionReason,
  reasonDetail: CandidateReasonText,
})]))();
export type CandidateTaskDecisionInput = typeof CandidateTaskDecisionInput.Type;

export const CandidateTaskDecision = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  version: Schema.Literal(CANDIDATE_DECISION_VERSION),
  taskId: TaskId,
  headCommitId: CommitId,
  decision: Schema.Literal("included"),
  reason: Schema.optionalKey(Schema.Never),
  reasonDetail: Schema.optionalKey(Schema.Never),
}), Schema.Struct({
  version: Schema.Literal(CANDIDATE_DECISION_VERSION),
  taskId: TaskId,
  headCommitId: CommitId,
  decision: Schema.Literal("omitted"),
  reason: CandidateOmissionReason,
  reasonDetail: CandidateReasonText,
})]))();
export type CandidateTaskDecision = typeof CandidateTaskDecision.Type;

export const CandidateResolutionInput = /* @__PURE__ */ (() => Schema.Struct({
  conflictId: CandidateToken.pipe(Schema.mutableKey),
  inputsDigest: Sha256Identity.pipe(Schema.mutableKey),
  resolutionDigest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type CandidateResolutionInput = typeof CandidateResolutionInput.Type;

export const CandidateResolutionIdentity = /* @__PURE__ */ (() => Schema.Struct({
  conflictId: CandidateToken,
  inputsDigest: Sha256Identity,
  resolutionDigest: Sha256Identity,
}))();
export type CandidateResolutionIdentity = typeof CandidateResolutionIdentity.Type;

export const CandidateManifestInput = /* @__PURE__ */ (() => Schema.Struct({
  changeId: Schema.String.pipe(Schema.mutableKey),
  baseCommitId: Schema.String.pipe(Schema.mutableKey),
  baseTreeId: Schema.String.pipe(Schema.mutableKey),
  targetRef: CandidateTargetRef.pipe(Schema.mutableKey),
  targetCommitId: Schema.String.pipe(Schema.mutableKey),
  targetRevision: CandidateTargetRevision.pipe(Schema.mutableKey),
  eligibility: Schema.Array(CandidateEligibilityInput).pipe(Schema.mutableKey),
  decisions: Schema.Array(CandidateTaskDecisionInput).pipe(Schema.mutableKey),
  resolutions: Schema.Array(CandidateResolutionInput).pipe(Schema.optional, Schema.mutableKey),
  compositionAlgorithm: CandidateToken.pipe(Schema.mutableKey),
  sourcePolicyDigest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type CandidateManifestInput = typeof CandidateManifestInput.Type;

export const CandidateManifest = /* @__PURE__ */ (() => Schema.Struct({
  version: Schema.Literal(CANDIDATE_MANIFEST_VERSION),
  changeId: ChangeId,
  baseCommitId: CommitId,
  baseTreeId: TreeId,
  target: Schema.Struct({
    ref: CandidateTargetRef,
    commitId: CommitId,
    revision: CandidateTargetRevision,
  }),
  eligibility: Schema.Struct({
    version: Schema.Literal(CANDIDATE_ELIGIBILITY_VERSION),
    entries: Schema.Array(CandidateEligibilityEntry),
    digest: Sha256Identity,
  }),
  decisions: Schema.Struct({
    version: Schema.Literal(CANDIDATE_DECISION_VERSION),
    entries: Schema.Array(CandidateTaskDecision),
    digest: Sha256Identity,
  }),
  resolutions: Schema.Array(CandidateResolutionIdentity),
  resolutionsDigest: Sha256Identity,
  compositionAlgorithm: CandidateToken,
  sourcePolicyDigest: Sha256Identity,
}))();
export type CandidateManifest = typeof CandidateManifest.Type;

export const CandidateManifestResult = /* @__PURE__ */ (() => Schema.Struct({
  candidateId: CandidateId,
  manifestDigest: Sha256Identity,
  manifest: CandidateManifest,
  canonicalJson: Schema.String,
}))();
export type CandidateManifestResult = typeof CandidateManifestResult.Type;

export const CandidateManifestError = /* @__PURE__ */ (() => {
  class CandidateManifestError extends privateDependencyFailure<CandidateManifestError>()("CandidateManifestError", "candidate manifest") {}
  return CandidateManifestError;
})();
export type CandidateManifestError = InstanceType<typeof CandidateManifestError>;
export const candidateManifestError = (cause: unknown) => new CandidateManifestError({ cause: Redacted.make(cause) });
